/**
 * InclusionControl — tri-state link inclusion toggle for the Communications UI
 * (EDG-8483, plan item 9).
 *
 * An email↔entity link is one of three states:
 *   - linked    (`included = true`)   — confirmed, shows in the entity's feed
 *   - suggested (`included = null`)    — awaiting a human decision
 *   - excluded  (`included = false`)   — explicitly dismissed
 *
 * Presentational only: it renders the current state plus its provenance and emits
 * `onChange` with the next inclusion value. The parent owns the PATCH mutation.
 * Styling stays within the design tokens (font ≤20px, radius ≤6px, token colours).
 */

import { Check, HelpCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type Inclusion = boolean | null;

export interface InclusionProvenance {
  linkSource?: string | null;
  confidence?: number | null;
  provenance?: Record<string, unknown> | null;
  entityLabel?: string | null;
}

interface InclusionControlProps extends InclusionProvenance {
  value: Inclusion;
  onChange: (next: Inclusion) => void;
  disabled?: boolean;
  testId?: string;
}

const STATES: Array<{
  key: "linked" | "suggested" | "excluded";
  value: Inclusion;
  label: string;
  icon: typeof Check;
  activeClass: string;
}> = [
  {
    key: "linked",
    value: true,
    label: "Linked",
    icon: Check,
    activeClass: "bg-success/15 text-success border-success/40",
  },
  {
    key: "suggested",
    value: null,
    label: "Suggested",
    icon: HelpCircle,
    activeClass: "bg-warning/15 text-warning border-warning/40",
  },
  {
    key: "excluded",
    value: false,
    label: "Excluded",
    icon: X,
    activeClass: "bg-destructive/10 text-destructive border-destructive/40",
  },
];

function sameInclusion(a: Inclusion, b: Inclusion) {
  return a === b || (a == null && b == null);
}

export function InclusionControl({
  value,
  onChange,
  disabled = false,
  linkSource,
  confidence,
  provenance,
  entityLabel,
  testId = "inclusion-control",
}: InclusionControlProps) {
  const hasProvenance =
    Boolean(linkSource) ||
    confidence != null ||
    (provenance != null && Object.keys(provenance).length > 0);

  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <div className="inline-flex items-center rounded-sm border bg-muted/30 p-0.5">
        {STATES.map((state) => {
          const active = sameInclusion(value, state.value);
          const Icon = state.icon;
          return (
            <button
              key={state.key}
              type="button"
              disabled={disabled}
              aria-pressed={active}
              onClick={() => onChange(state.value)}
              className={cn(
                "inline-flex items-center gap-1 rounded-sm border border-transparent px-1.5 py-0.5 text-xs font-medium transition-colors",
                "hover:bg-background disabled:cursor-not-allowed disabled:opacity-50",
                active ? state.activeClass : "text-muted-foreground",
              )}
              data-testid={`${testId}-${state.key}`}
              data-active={active}
            >
              <Icon className="h-3 w-3" />
              {state.label}
            </button>
          );
        })}
      </div>

      {hasProvenance ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
              aria-label="Link provenance"
              data-testid={`${testId}-provenance`}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 space-y-1.5 text-xs">
            <p className="font-medium">Why this is suggested</p>
            {entityLabel ? (
              <p className="text-muted-foreground">
                Entity: <span className="text-foreground">{entityLabel}</span>
              </p>
            ) : null}
            {linkSource ? (
              <p className="text-muted-foreground">
                Source: <span className="text-foreground">{linkSource.replace(/_/g, " ")}</span>
              </p>
            ) : null}
            {confidence != null ? (
              <p className="text-muted-foreground">
                Confidence: <span className="text-foreground">{confidence}%</span>
              </p>
            ) : null}
            {provenance && Object.keys(provenance).length > 0 ? (
              <dl className="space-y-0.5 border-t pt-1.5">
                {Object.entries(provenance).map(([key, val]) => (
                  <div key={key} className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">{key}</dt>
                    <dd className="truncate text-foreground">{String(val)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
